import { HttpClient, HttpHeaders } from "@angular/common/http";
import { inject, Injectable, signal } from "@angular/core";
import { LoginAuthOTPRequest, LoginAuthOTPResponse, LoginInitRequest, LoginInitResponse, LoginJWTTokenRequest, LoginJWTTokenResponse, LoginMessage, LoginParamsRequest, LoginParamsResponse } from "../../interfaces/loginOtp-interface";
import { catchError, EMPTY, map, Observable, of, switchMap, tap } from "rxjs";
import { AUTH_KEYS, AuthProvider, AuthResponse, LoginType } from "../../interfaces/auth-response";
import { Router } from "@angular/router";
import { EnvironmentService } from "../../services/environment.service";
import { CiamRefreshService } from "../../services/ciam-refresh.service";

@Injectable({ providedIn: 'root' })
export class AuthOtpProvider implements AuthProvider {
    showOtpStep = signal<boolean>(false);
    private environmentService = inject(EnvironmentService);

    paramsResponseData: LoginParamsResponse | null = null;
    _isAuthenticated = false;
    selectedAuthenticator: string | null = null;

    constructor(
        private http: HttpClient,
        private router: Router,
        private ciamRefreshService: CiamRefreshService
    ) { }

    private createSessionId(): string {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }

        return `session-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }

    login(creds: {username: string, password: string, authenticator: string}): Observable<any> {
        return this.startLoginOtpFlow(creds.username, creds.password, creds.authenticator);
    }

    // Metodo specifico per lo step 2 (OTP)
    verifyOtp(code: string): Observable<any> {
        return this.submitOtp(code);
    }

    getToken(): Observable<string | null> {
        return this.ciamRefreshService.getToken().pipe(
            catchError(() => {
                this.logout();
                return EMPTY;
            })
        );
    }

    private saveSession(res: AuthResponse) {
        localStorage.setItem(AUTH_KEYS.LOGIN_TYPE, LoginType.OTP);
        localStorage.setItem(AUTH_KEYS.ACCESS_TOKEN, res.accessToken);
        if (res.refreshToken) {
            localStorage.setItem(AUTH_KEYS.REFRESH_TOKEN, res.refreshToken);
        }
        localStorage.setItem(
            AUTH_KEYS.EXPIRES_AT,
            (Date.now() + res.expiresAt * 1000).toString()
        );
    }

    isLoggedIn(): Observable<boolean> { return of(!!localStorage.getItem('access_token')); }

    logout() {
        localStorage.removeItem(AUTH_KEYS.LOGIN_TYPE);
        localStorage.removeItem(AUTH_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(AUTH_KEYS.REFRESH_TOKEN);
        localStorage.removeItem(AUTH_KEYS.EXPIRES_AT);
        this.resetFlowState();
        this.router.navigate(['/login']);
    }

    private loginInit(request: LoginInitRequest): Observable<LoginInitResponse> {
        const url = this.buildCiamUrl('/init');
        return this.http.post<LoginInitResponse>(url, request);
    }

    private loginParams(request: LoginParamsRequest): Observable<LoginParamsResponse> {
        const url = this.buildCiamUrl('/auth');
        return this.http.post<LoginParamsResponse>(url, request);
    }

    private loginAuthOTP(request: LoginAuthOTPRequest): Observable<LoginAuthOTPResponse> {
        const url = this.buildCiamUrl('/auth');
        return this.http.post<LoginAuthOTPResponse>(url, request);
    }

    private loginJWTToken(request: LoginJWTTokenRequest): Observable<LoginJWTTokenResponse> {
        const url = this.buildCiamUrl('/1.0.0/token');

        const headers = new HttpHeaders({
            Authorization: this.environmentService.ciamTokenAuthHeader()
        });

        return this.http.post<LoginJWTTokenResponse>(url, request, { headers });
    }

    // First
    private startLoginOtpFlow(username: string, password: string, authenticatorName: string): Observable<LoginParamsResponse> {
        this.resetFlowState();
        this.selectedAuthenticator = authenticatorName;

        const initBody: LoginInitRequest = {
            clientid: this.environmentService.ciamClientId(),
            scope: this.environmentService.ciamScope(),
            sessionid: this.createSessionId()
        };

        return this.loginInit(initBody).pipe(
            switchMap((initResponse) => {
                const flowId = initResponse.flowId;
                const authenticator = this.getAuthenticatorByName(initResponse, authenticatorName);

                if (!authenticator) {
                    throw new Error(`Autenticatore ${authenticatorName} non trovato.`);
                }

                const paramsBody: LoginParamsRequest = {
                    flowId,
                    selectedAuthenticator: {
                        authenticatorId: authenticator.authenticatorId,
                        params: {
                            username,
                            password
                        }
                    }
                };

                return this.loginParams(paramsBody).pipe(
                    map((paramsResponse) => this.ensureNoErrorMessages(paramsResponse))
                );
            }),
            tap((paramsResponse) => {
                console.log("paramsResponse",paramsResponse)
                this.paramsResponseData = paramsResponse;
                this.showOtpStep.set(true);
            }),
            // map((paramsResponse): AuthStatus => {
            //     return {
            //         success: true,
            //         nextStep: 'OTP', // Comunichiamo alla UI che ora serve l'OTP
            //     };
            // }),
        );
    }

    // Second
    private submitOtp(otpCode: string): Observable<LoginJWTTokenResponse> {
        if (!otpCode.trim()) {
            throw new Error('Inserisci il codice OTP.');
        }

        if (!this.paramsResponseData) {
            throw new Error('Dati del secondo step non disponibili.');
        }

        if (!this.selectedAuthenticator) {
            throw new Error('Authenticator selezionato non disponibile.');
        }

        const authenticator = this.getAuthenticatorByName(this.paramsResponseData, this.selectedAuthenticator)
            ?? this.paramsResponseData?.nextStep?.authenticators?.[0];

        if (!authenticator) {
            throw new Error(`Autenticatore ${this.selectedAuthenticator} non trovato per lo step OTP.`);
        }

        const authOtpBody: LoginAuthOTPRequest = {
            flowId: this.paramsResponseData.flowId,
            selectedAuthenticator: {
                authenticatorId: authenticator.authenticatorId,
                params: {
                    OTPcode: otpCode
                }
            }
        };

        return this.loginAuthOTP(authOtpBody).pipe(
            switchMap((authOtpResponse) => {
                if (authOtpResponse.flowStatus !== 'SUCCESS_COMPLETED') {
                    throw new Error('Flusso OTP non completato correttamente.');
                }

                const jwtTokenBody: LoginJWTTokenRequest = {
                    granttype: 'authorization_code',
                    code: authOtpResponse.authData.code,
                    scope: this.environmentService.ciamScope()
                };

                return this.loginJWTToken(jwtTokenBody);
            }),
            map(res => {
                // console.log("jwtTokenResponse res",res)
                return res;
            }),
            tap((jwtTokenResponse) => {
                console.log("jwtTokenResponse",jwtTokenResponse)
                this.saveSession({
                    accessToken: jwtTokenResponse.access_token,
                    refreshToken: jwtTokenResponse.refresh_token,
                    expiresAt: jwtTokenResponse.expires_in
                });

                this.resetFlowState();
            }),
            // map((paramsResponse): AuthStatus => {
            //     return {
            //         success: true,
            //         nextStep: 'HOME', // Comunichiamo alla UI di andare a home
            //     };
            // }),
        );
    }

    resetFlowState() {
        this.showOtpStep.set(false);
        this.paramsResponseData = null;
        this.selectedAuthenticator = null;
    }

    private getAuthenticatorByName(data: any, authenticatorName: string) {
        return data?.nextStep?.authenticators?.find((auth: any) => auth.authenticator === authenticatorName);
    }

    ensureNoErrorMessages(response: LoginParamsResponse): LoginParamsResponse {
        const errorMessage = response.nextStep?.messages?.find((message: LoginMessage) => message.type === 'ERROR');

        if (errorMessage) {
            throw new Error(errorMessage.message);
        }

        return response;
    }

    private buildCiamUrl(path: string): string {
        const baseUrl = this.environmentService.ciamBaseUrl().trim().replace(/\/+$/, '');

        if (!baseUrl) {
            throw new Error("CIAM base URL non configurato per l'ambiente corrente.");
        }

        return `${baseUrl}${path}`;
    }
}
